import {
  ListManager, PageHeader, Paginator, Styles,
} from '@/components'
import useAuth from '@/hooks/useAuth'
import useAxiosRequest from '@/hooks/useAxiosRequest'
import usePageTitle from '@/hooks/usePageTitle'
import useQuery from '@/hooks/useQuery'
import castrations from '@/services/api/castrations'
import clinicalCares from '@/services/api/clinicalCares'
import PropTypes from 'prop-types'
import React, { useCallback, useMemo } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import MessagesFilter from './components/Filter'
import Row from './components/Row'
import MessagesModal from './Modal'

function onlyUnreadFilterIsActive(query) {
  return query?.q?.onlyUnread === true || query?.q?.onlyUnread === 'true'
}

function Messages({ title, type, basePath }) {
  usePageTitle({ title })
  const query = useQuery()
  const history = useHistory()
  const location = useLocation()
  const { conversationId } = useParams()
  const { licensing } = useAuth()
  const fetchList = useCallback((params) => {
    if (type === 'castration') return castrations.conversations(params)
    return clinicalCares.conversations(params)
  }, [type])
  const messageService = useMemo(() => ({
    history(conversation, params) {
      if (type === 'castration') return castrations.messages(conversation.resourceId, params)
      return clinicalCares.messages(conversation.resourceId, params)
    },
    addMessage(conversation, body) {
      if (type === 'castration') return castrations.addMessage(conversation.resourceId, body)
      return clinicalCares.addMessage(conversation.resourceId, body)
    },
    read(conversation) {
      if (type === 'castration') return castrations.readMessages(conversation.resourceId)
      return clinicalCares.readMessages(conversation.resourceId)
    },
  }), [type])
  const {
    data, error, loading, setData,
  } = useAxiosRequest(fetchList, query)
  const onlyUnread = onlyUnreadFilterIsActive(query)

  const selected = useMemo(() => {
    const selectedFromList = data?.rows?.find((conversation) => (
      Number(conversation.conversationId) === Number(conversationId)
    ))
    const selectedFromLocation = location.state?.conversation

    if (selectedFromList) return selectedFromList
    if (Number(selectedFromLocation?.conversationId) === Number(conversationId)) {
      return selectedFromLocation
    }

    return null
  }, [conversationId, data, location.state])

  const updateConversation = useCallback((conversationIdToUpdate, mapper) => {
    setData((current) => (
      current
        ? {
          ...current,
          rows: current.rows
            ?.map((item) => (
              Number(item.conversationId) === Number(conversationIdToUpdate)
                ? mapper(item)
                : item
            ))
            .filter((item) => (
              !onlyUnread
              || Number(item.conversationId) !== Number(conversationIdToUpdate)
              || item.unreads > 0
            )),
        }
        : current
    ))
  }, [onlyUnread, setData])

  const readConversation = useCallback(async (conversation) => {
    if (!conversation?.unreads) return

    updateConversation(conversation.conversationId, (item) => ({ ...item, unreads: 0 }))

    try {
      await messageService.read(conversation)
    } catch (e) {
      toast.error(e?.message)
    }
  }, [messageService, updateConversation])

  const updateConversationLastMessage = useCallback((conversation, message) => {
    if (!message?.id) return
    const lastMessage = {
      ...message,
      attachmentURL: message.attachmentURL || message.images?.[0],
    }

    updateConversation(conversation.conversationId, (item) => ({
      ...item,
      unreads: 0,
      lastMessage,
    }))
  }, [updateConversation])

  function closeModal() {
    history.push(`${basePath}${location.search}`)
  }

  return (
    <Styles.Container className="grid-xl">
      <PageHeader
        title="Mensagens"
        subtitle="Visualize e responda às mensagens recebidas"
        icon="fas fa-comments"
        rightAction={() => <MessagesFilter type={type} />}
      />
      <ListManager
        loading={loading}
        count={data?.count || 0}
        error={error}
        emptyMessage="Nenhuma conversa"
        emptySubtitle="Ainda não há mensagens recebidas"
        emptyIcon="fas fa-comments"
      >
        {
          Boolean(data) && (
            <div>
              <small>
                <table className="table table-striped table-hover">
                  <thead>
                    <tr>
                      <th>Assunto</th>
                      <th>Última mensagem</th>
                      <th>Participantes</th>
                      <th>Status</th>
                      <th width={1} className="text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((conversation) => (
                      <Row
                        key={`${conversation.type}-${conversation.conversationId}`}
                        conversation={conversation}
                        basePath={basePath}
                        search={location.search}
                      />
                    ))}
                  </tbody>
                </table>
              </small>
              <Paginator paginatedList={data} />
            </div>
          )
        }
      </ListManager>
      {
        conversationId && (
          <MessagesModal
            selected={selected}
            listLoading={loading && !selected}
            messageService={messageService}
            onClose={closeModal}
            onRead={readConversation}
            onMessageAdded={(message) => updateConversationLastMessage(selected, message)}
            licensing={licensing}
          />
        )
      }
    </Styles.Container>
  )
}

Messages.propTypes = {
  title: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['castration', 'clinicalCare']).isRequired,
  basePath: PropTypes.string.isRequired,
}

export default Messages
